/* ==========================================================================
   DEEB CIGARS - First Cigar Guide
   File: assets/deeb-first-cigar-guide.js

   Responsibilities:
   - Screen navigation
   - Progress and status updates
   - Accessible answer selection
   - Product scoring
   - Recommendation rendering
   - Shopify Theme Editor re-initialization
   ========================================================================== */

(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-deeb-first-guide]',
    screen: '[data-guide-screen]',
    start: '[data-guide-start]',
    answer: '[data-guide-answer]',
    back: '[data-guide-back]',
    restart: '[data-guide-restart]',
    topRestart: '.deeb-first-guide__restart',
    progress: '[data-guide-progress]',
    progressBar: '[data-guide-progressbar]',
    progressFill: '[data-guide-progress-fill]',
    progressCount: '[data-guide-progress-count]',
    stepLabel: '[data-guide-step-label]',
    navigation: '[data-guide-navigation]',
    selectionNote: '.deeb-first-guide__selection-note',
    results: '[data-guide-results]',
    resultsSummary: '[data-guide-results-summary]',
    empty: '[data-guide-empty]',
    productsJson: '[data-deeb-guide-products]',
    configJson: '[data-deeb-guide-config]'
  };

  const STEP_ORDER = [
    'budget',
    'time',
    'strength',
    'flavor'
  ];

  const STEP_LABELS = {
    budget: 'Question 1',
    time: 'Question 2',
    strength: 'Question 3',
    flavor: 'Question 4'
  };

  const RESULT_LABELS = [
    'Best match',
    'Strong alternative',
    'Another good fit',
    'Discovery option'
  ];

  /*
   * Product strength metafield values use a 1-5 scale.
   * The guide now presents three beginner-friendly choices.
   *
   * Mild-Medium favors levels 1-2 and accepts level 3 as a close match.
   * Medium targets level 3, with levels 2 and 4 treated as close matches.
   * Medium-Full favors levels 4-5 and accepts level 3 as a close match.
   */
  const STRENGTH_PROFILES = {
    'mild-medium': {
      exact: [1, 2],
      close: [3],
      partial: [],
      avoid: [4, 5]
    },

    medium: {
      exact: [3],
      close: [2, 4],
      partial: [1, 5],
      avoid: []
    },

    'medium-full': {
      exact: [4, 5],
      close: [3],
      partial: [2],
      avoid: []
    }
  };

  const FLAVOR_KEYWORDS = {
    'creamy-smooth': [
      'cream',
      'creamy',
      'butter',
      'buttery',
      'vanilla',
      'smooth',
      'silky',
      'milk',
      'mellow',
      'soft'
    ],

    'woody-nutty': [
      'wood',
      'woody',
      'cedar',
      'oak',
      'mahogany',
      'dry wood',
      'nut',
      'nuts',
      'nutty',
      'walnut',
      'almond',
      'cashew',
      'toasted',
      'toast'
    ],

    'spicy-rich': [
      'spice',
      'spicy',
      'pepper',
      'peppery',
      'black pepper',
      'white pepper',
      'sweet pepper',
      'cinnamon',
      'chocolate',
      'cocoa',
      'coffee',
      'espresso',
      'caramel',
      'molasses',
      'earth',
      'earthy',
      'leather',
      'dark sweetness',
      'rich'
    ]
  };

  const parseJson = (element, fallback) => {
    if (!element) {
      return fallback;
    }

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error(
        'Deeb guide JSON could not be parsed.',
        error
      );

      return fallback;
    }
  };

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : fallback;
  };

  const toBoolean = (value) => {
    if (typeof value === 'boolean') {
      return value;
    }

    return String(value).toLowerCase() === 'true';
  };

  const normalizeText = (value) => {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[•·|,/]+/g, ' ')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const clamp = (value, min, max) => {
    return Math.max(
      min,
      Math.min(value, max)
    );
  };

  const containsAnyKeyword = (text, keywords) => {
    return keywords.some((keyword) => {
      return text.includes(keyword);
    });
  };

  const getEffectiveRangeMaximum = (answer) => {
    if (!answer) {
      return 0;
    }

    /*
     * The first two budget ranges and the first time range are
     * represented in Liquid with an upper boundary one unit below
     * the next range. Adding one here keeps decimal prices safe
     * without creating overlap at the displayed boundary.
     */
    const usesExclusiveUpperBoundary = [
      'budget-1',
      'budget-2',
      'quick'
    ].includes(answer.value);

    return usesExclusiveUpperBoundary
      ? answer.max + 1
      : answer.max;
  };

  const isWithinAnswerRange = (value, answer) => {
    if (!answer) {
      return false;
    }

    const maximum =
      getEffectiveRangeMaximum(answer);

    const usesExclusiveUpperBoundary = [
      'budget-1',
      'budget-2',
      'quick'
    ].includes(answer.value);

    if (usesExclusiveUpperBoundary) {
      return (
        value >= answer.min &&
        value < maximum
      );
    }

    return (
      value >= answer.min &&
      value <= maximum
    );
  };

  const initGuide = (root) => {
    if (
      !root ||
      root.dataset.guideReady === 'true'
    ) {
      return;
    }

    const screens = Array.from(
      root.querySelectorAll(SELECTORS.screen)
    );

    const startButton = root.querySelector(
      SELECTORS.start
    );

    const answerButtons = Array.from(
      root.querySelectorAll(SELECTORS.answer)
    );

    const backButton = root.querySelector(
      SELECTORS.back
    );

    const restartButtons = Array.from(
      root.querySelectorAll(SELECTORS.restart)
    );

    const topRestart = root.querySelector(
      SELECTORS.topRestart
    );

    const progress = root.querySelector(
      SELECTORS.progress
    );

    const progressBar = root.querySelector(
      SELECTORS.progressBar
    );

    const progressFill = root.querySelector(
      SELECTORS.progressFill
    );

    const progressCount = root.querySelector(
      SELECTORS.progressCount
    );

    const stepLabel = root.querySelector(
      SELECTORS.stepLabel
    );

    const navigation = root.querySelector(
      SELECTORS.navigation
    );

    const selectionNote = root.querySelector(
      SELECTORS.selectionNote
    );

    const resultsContainer = root.querySelector(
      SELECTORS.results
    );

    const resultsSummary = root.querySelector(
      SELECTORS.resultsSummary
    );

    const emptyResults = root.querySelector(
      SELECTORS.empty
    );

    if (
      !screens.length ||
      !startButton ||
      !answerButtons.length
    ) {
      return;
    }

    const products = parseJson(
      root.querySelector(
        SELECTORS.productsJson
      ),
      []
    );

    const config = parseJson(
      root.querySelector(
        SELECTORS.configJson
      ),
      {}
    );

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    const state = {
      currentStep: 0,
      answers: {},
      transitionTimer: null,
      loadingTimer: null
    };

    root.dataset.guideReady = 'true';

    const clearTimers = () => {
      window.clearTimeout(
        state.transitionTimer
      );

      window.clearTimeout(
        state.loadingTimer
      );
    };

    const setStatus = (message) => {
      if (selectionNote) {
        selectionNote.textContent = message;
      }
    };

    const getScreen = (name) => {
      return screens.find((screen) => {
        return (
          screen.dataset.guideScreen === name
        );
      });
    };

    const scrollToGuide = () => {
      const rect =
        root.getBoundingClientRect();

      const targetTop =
        window.scrollY +
        rect.top -
        8;

      if (Math.abs(rect.top) < 18) {
        return;
      }

      window.scrollTo({
        top: targetTop,
        behavior: reducedMotion.matches
          ? 'auto'
          : 'smooth'
      });
    };

    const focusScreenHeading = (screen) => {
      if (!screen) {
        return;
      }

      const heading = screen.querySelector(
        'h1, h2'
      );

      if (!heading) {
        return;
      }

      heading.setAttribute(
        'tabindex',
        '-1'
      );

      window.requestAnimationFrame(() => {
        heading.focus({
          preventScroll: true
        });
      });
    };

    const updateProgress = () => {
      const visibleStep =
        state.currentStep + 1;

      const percent =
        (
          visibleStep /
          STEP_ORDER.length
        ) *
        100;

      const screenName =
        STEP_ORDER[state.currentStep];

      if (progressCount) {
        progressCount.textContent =
          `${visibleStep} / ${STEP_ORDER.length}`;
      }

      if (stepLabel) {
        stepLabel.textContent =
          STEP_LABELS[screenName] ||
          `Question ${visibleStep}`;
      }

      if (progressFill) {
        progressFill.style.width =
          `${percent}%`;
      }

      if (progressBar) {
        progressBar.setAttribute(
          'aria-valuenow',
          String(visibleStep)
        );
      }
    };

    const updateBackVisibility = () => {
      if (!backButton) {
        return;
      }

      /*
       * The first question has no previous question.
       * Hide the control cleanly instead of showing an action
       * whose label would be misleading.
       */
      backButton.hidden =
        state.currentStep === 0;
    };

    const showScreen = (
      name,
      options = {}
    ) => {
      const {
        focus = true,
        scroll = true
      } = options;

      let activeScreen = null;

      screens.forEach((screen) => {
        const isTarget =
          screen.dataset.guideScreen === name;

        screen.hidden = !isTarget;

        screen.classList.toggle(
          'is-active',
          isTarget
        );

        if (isTarget) {
          activeScreen = screen;
        }
      });

      const isQuestion =
        STEP_ORDER.includes(name);

      const isIntro =
        name === 'intro';

      const isLoading =
        name === 'loading';

      if (progress) {
        progress.hidden = !isQuestion;
      }

      if (navigation) {
        navigation.hidden = !isQuestion;
      }

      if (topRestart) {
        topRestart.hidden =
          isIntro || isLoading;
      }

      if (focus) {
        focusScreenHeading(activeScreen);
      }

      if (scroll) {
        scrollToGuide();
      }
    };

    const syncAnswerSelection = (
      dimension
    ) => {
      const savedAnswer =
        state.answers[dimension];

      answerButtons
        .filter((button) => {
          return (
            button.dataset.dimension ===
            dimension
          );
        })
        .forEach((button) => {
          const isSelected =
            savedAnswer &&
            button.dataset.value ===
              savedAnswer.value;

          button.classList.toggle(
            'is-selected',
            Boolean(isSelected)
          );

          button.setAttribute(
            'aria-checked',
            isSelected
              ? 'true'
              : 'false'
          );
        });
    };

    const showCurrentQuestion = () => {
      const screenName =
        STEP_ORDER[state.currentStep];

      const savedAnswer =
        state.answers[screenName];

      updateProgress();
      updateBackVisibility();

      syncAnswerSelection(screenName);

      showScreen(screenName);

      setStatus(
        savedAnswer
          ? 'Previous answer saved — choose an answer to continue'
          : 'Select one answer to continue'
      );
    };

    const getAnswerLabel = (button) => {
      const label =
        button.querySelector('strong');

      return label
        ? label.textContent
            .trim()
            .replace(/\s+/g, ' ')
        : button.dataset.value;
    };

    const saveAnswer = (button) => {
      const dimension =
        button.dataset.dimension;

      const value =
        button.dataset.value;

      const groupButtons =
        answerButtons.filter((candidate) => {
          return (
            candidate.dataset.dimension ===
            dimension
          );
        });

      groupButtons.forEach((candidate) => {
        const isSelected =
          candidate === button;

        candidate.classList.toggle(
          'is-selected',
          isSelected
        );

        candidate.setAttribute(
          'aria-checked',
          isSelected
            ? 'true'
            : 'false'
        );
      });

      state.answers[dimension] = {
        value,
        label: getAnswerLabel(button),
        min: toNumber(
          button.dataset.min
        ),
        max: toNumber(
          button.dataset.max
        )
      };

      setStatus(
        'Answer saved — continuing'
      );
    };

    const scoreBudget = (
      product,
      answer,
      result
    ) => {
      if (!answer) {
        return;
      }

      /*
       * Shopify prices are supplied in minor units.
       *
       * Example:
       * 150000 becomes 1500.00 EGP.
       */
      const price =
        toNumber(product.price) / 100;

      if (!price) {
        return;
      }

      if (
        isWithinAnswerRange(
          price,
          answer
        )
      ) {
        result.score += 6;

        result.reasons.push(
          'Within your budget'
        );

        return;
      }

      const effectiveMaximum =
        getEffectiveRangeMaximum(answer);

      const distance =
        price < answer.min
          ? answer.min - price
          : price - effectiveMaximum;

      const allowedDistance =
        Math.max(
          effectiveMaximum * 0.18,
          200
        );

      if (
        distance <= allowedDistance
      ) {
        result.score += 2;

        result.reasons.push(
          'Close to your budget'
        );
      } else if (
        distance >
        effectiveMaximum * 0.65
      ) {
        result.score -= 1;
      }
    };

    const scoreTime = (
      product,
      answer,
      result
    ) => {
      if (!answer) {
        return;
      }

      const minutes = toNumber(
        product.smokingTimeMinutes
      );

      if (!minutes) {
        return;
      }

      if (
        isWithinAnswerRange(
          minutes,
          answer
        )
      ) {
        result.score += 5;

        result.reasons.push(
          product.smokingTimeLabel ||
          `${minutes} minute smoke`
        );

        return;
      }

      const effectiveMaximum =
        getEffectiveRangeMaximum(answer);

      const distance =
        minutes < answer.min
          ? answer.min - minutes
          : minutes - effectiveMaximum;

      if (distance <= 10) {
        result.score += 3;

        result.reasons.push(
          'Close to your available time'
        );
      } else if (distance <= 20) {
        result.score += 1;
      }
    };

    const scoreStrength = (
      product,
      answer,
      result
    ) => {
      if (!answer) {
        return;
      }

      const productStrength =
        toNumber(
          product.strengthLevel
        );

      const profile =
        STRENGTH_PROFILES[answer.value];

      if (
        !productStrength ||
        !profile
      ) {
        return;
      }

      if (
        profile.exact.includes(
          productStrength
        )
      ) {
        result.score += 6;

        result.reasons.push(
          product.strengthLabel ||
          'Your preferred strength'
        );
      } else if (
        profile.close.includes(
          productStrength
        )
      ) {
        result.score += 3;

        result.reasons.push(
          'Close strength match'
        );
      } else if (
        profile.partial.includes(
          productStrength
        )
      ) {
        result.score += 1;
      }

      /*
       * Protect beginners who select Mild from recommendations
       * with a clearly stronger nicotine profile.
       */
      if (
        profile.avoid.includes(
          productStrength
        )
      ) {
        result.score -= 4;
      }
    };

    const scoreFlavor = (
      product,
      answer,
      result
    ) => {
      if (!answer) {
        return;
      }

      const family =
        normalizeText(
          product.flavorFamily
        );

      const notes =
        normalizeText(
          product.flavorNotes
        );

      const keywords =
        FLAVOR_KEYWORDS[answer.value] ||
        [answer.value];

      if (
        containsAnyKeyword(
          family,
          keywords
        )
      ) {
        result.score += 6;

        result.reasons.push(
          `${answer.label} profile`
        );
      }

      if (
        containsAnyKeyword(
          notes,
          keywords
        )
      ) {
        result.score += 3;

        if (
          !result.reasons.includes(
            `${answer.label} profile`
          )
        ) {
          result.reasons.push(
            `${answer.label} tasting notes`
          );
        }
      }
    };

    const scoreBeginnerSuitability = (
      product,
      result
    ) => {
      const beginnerFriendly =
        toBoolean(
          product.beginnerFriendly
        );

      if (beginnerFriendly) {
        result.score += 4;

        result.reasons.push(
          'Beginner friendly'
        );
      }

      const strength =
        toNumber(
          product.strengthLevel
        );

      if (
        !beginnerFriendly &&
        strength >= 5
      ) {
        result.score -= 2;
      }
    };

    const scoreAvailability = (
      product,
      result
    ) => {
      if (
        product.available ||
        product.variantAvailable
      ) {
        result.score += 1;
      }
    };

    const scoreProduct = (
      product,
      index
    ) => {
      const result = {
        product,
        index,
        score: 0,
        reasons: []
      };

      scoreBudget(
        product,
        state.answers.budget,
        result
      );

      scoreTime(
        product,
        state.answers.time,
        result
      );

      scoreStrength(
        product,
        state.answers.strength,
        result
      );

      scoreFlavor(
        product,
        state.answers.flavor,
        result
      );

      scoreBeginnerSuitability(
        product,
        result
      );

      scoreAvailability(
        product,
        result
      );

      result.reasons = Array.from(
        new Set(result.reasons)
      ).slice(0, 4);

      return result;
    };

    const sortResults = (
      left,
      right
    ) => {
      if (
        right.score !== left.score
      ) {
        return (
          right.score -
          left.score
        );
      }

      const rightBeginner =
        toBoolean(
          right.product.beginnerFriendly
        );

      const leftBeginner =
        toBoolean(
          left.product.beginnerFriendly
        );

      if (
        rightBeginner !== leftBeginner
      ) {
        return (
          Number(rightBeginner) -
          Number(leftBeginner)
        );
      }

      const rightAvailable =
        Boolean(
          right.product.available ||
          right.product.variantAvailable
        );

      const leftAvailable =
        Boolean(
          left.product.available ||
          left.product.variantAvailable
        );

      if (
        rightAvailable !== leftAvailable
      ) {
        return (
          Number(rightAvailable) -
          Number(leftAvailable)
        );
      }

      return left.index - right.index;
    };

    const buildProductMeta = (
      product
    ) => {
      return [
        product.strengthLabel,
        product.smokingTimeLabel,
        product.originGroup
      ]
        .filter(Boolean)
        .join(' · ');
    };

    const getFallbackReasons = (
      product
    ) => {
      const reasons = [];

      if (
        toBoolean(
          product.beginnerFriendly
        )
      ) {
        reasons.push(
          'Beginner friendly'
        );
      }

      if (product.strengthLabel) {
        reasons.push(
          product.strengthLabel
        );
      }

      if (
        product.smokingTimeLabel
      ) {
        reasons.push(
          product.smokingTimeLabel
        );
      }

      return reasons.slice(0, 3);
    };

    const createResultCard = (
      result,
      rank
    ) => {
      const product = result.product;

      const productUrl =
        product.url ||
        (product.handle
          ? `/products/${product.handle}`
          : '#');

      const reasons =
        result.reasons.length
          ? result.reasons
          : getFallbackReasons(
              product
            );

      const isAvailable =
        Boolean(
          product.available ||
          product.variantAvailable
        );

      const imageMarkup =
        product.image
          ? `
            <img
              src="${escapeHtml(product.image)}"
              alt="${escapeHtml(
                product.imageAlt ||
                product.title
              )}"
              loading="lazy"
              width="1200"
              height="900"
            >
          `
          : `
            <div
              class="deeb-first-guide__result-placeholder"
              aria-hidden="true"
            >
              ${String(rank + 1).padStart(2, '0')}
            </div>
          `;

      const reasonsMarkup =
        reasons
          .map((reason) => {
            return `
              <li>
                ${escapeHtml(reason)}
              </li>
            `;
          })
          .join('');

      const meta =
        buildProductMeta(product);

      const availabilityMarkup =
        isAvailable
          ? ''
          : '<span>Currently unavailable</span>';

      return `
        <a
          class="deeb-first-guide__result-card"
          href="${escapeHtml(productUrl)}"
          aria-label="View ${escapeHtml(product.title)}"
        >
          <div class="deeb-first-guide__result-media">
            ${imageMarkup}

            <span class="deeb-first-guide__result-rank">
              ${escapeHtml(
                RESULT_LABELS[rank] ||
                `Match ${rank + 1}`
              )}
            </span>
          </div>

          <div class="deeb-first-guide__result-content">
            <p class="deeb-first-guide__result-kicker">
              ${escapeHtml(
                product.vendor ||
                product.originGroup ||
                'Deeb selection'
              )}
            </p>

            <h3 class="deeb-first-guide__result-title">
              ${escapeHtml(
                product.title
              )}
            </h3>

            ${
              reasonsMarkup
                ? `
                  <ul class="deeb-first-guide__result-reasons">
                    ${reasonsMarkup}
                  </ul>
                `
                : ''
            }

            ${
              meta || availabilityMarkup
                ? `
                  <p class="deeb-first-guide__result-meta">
                    ${escapeHtml(meta)}

                    ${
                      meta &&
                      availabilityMarkup
                        ? ' · '
                        : ''
                    }

                    ${availabilityMarkup}
                  </p>
                `
                : ''
            }

            <div class="deeb-first-guide__result-bottom">
              <span class="deeb-first-guide__result-price">
                ${escapeHtml(
                  product.formattedPrice ||
                  ''
                )}
              </span>

              <span class="deeb-first-guide__result-link">
                View cigar
              </span>
            </div>
          </div>
        </a>
      `;
    };

    const updateResultsSummary = () => {
      if (!resultsSummary) {
        return;
      }

      const summaryParts = [
        state.answers.strength?.label,
        state.answers.time?.label,
        state.answers.flavor?.label
      ].filter(Boolean);

      if (!summaryParts.length) {
        resultsSummary.textContent =
          'We matched your answers with the strongest available options.';

        return;
      }

      resultsSummary.textContent =
        `Built around ${summaryParts.join(', ')}.`;
    };

    const renderResults = () => {
      if (
        !resultsContainer ||
        !emptyResults
      ) {
        return;
      }

      const allCandidates =
        products.filter((product) => {
          return (
            product &&
            product.id
          );
        });

      updateResultsSummary();

      if (!allCandidates.length) {
        resultsContainer.innerHTML = '';
        resultsContainer.hidden = true;
        emptyResults.hidden = false;

        return;
      }

      const availableCandidates =
        allCandidates.filter((product) => {
          return (
            product.available ||
            product.variantAvailable
          );
        });

      /*
       * During development, products may exist
       * without tracked inventory.
       *
       * If none are available, keep the guide
       * testable using the full product pool.
       */
      const candidatePool =
        availableCandidates.length
          ? availableCandidates
          : allCandidates;

      const requestedLimit =
        clamp(
          toNumber(
            config.resultsLimit,
            3
          ),
          1,
          4
        );

      const topResults =
        candidatePool
          .map(scoreProduct)
          .sort(sortResults)
          .slice(
            0,
            requestedLimit
          );

      if (!topResults.length) {
        resultsContainer.innerHTML = '';
        resultsContainer.hidden = true;
        emptyResults.hidden = false;

        return;
      }

      resultsContainer.innerHTML =
        topResults
          .map(createResultCard)
          .join('');

      resultsContainer.hidden = false;
      emptyResults.hidden = true;
    };

    const showResults = () => {
      renderResults();

      showScreen('results');
    };

    const finishGuide = () => {
      showScreen('loading');

      window.clearTimeout(
        state.loadingTimer
      );

      state.loadingTimer =
        window.setTimeout(
          showResults,
          reducedMotion.matches
            ? 40
            : 850
        );
    };

    const moveNext = () => {
      if (
        state.currentStep <
        STEP_ORDER.length - 1
      ) {
        state.currentStep += 1;

        showCurrentQuestion();

        return;
      }

      finishGuide();
    };

    const handleAnswerClick = (
      button
    ) => {
      clearTimers();

      saveAnswer(button);

      state.transitionTimer =
        window.setTimeout(
          moveNext,
          reducedMotion.matches
            ? 30
            : 380
        );
    };

    const resetGuide = () => {
      clearTimers();

      state.currentStep = 0;
      state.answers = {};

      answerButtons.forEach((button) => {
        button.classList.remove(
          'is-selected'
        );

        button.setAttribute(
          'aria-checked',
          'false'
        );
      });

      if (resultsContainer) {
        resultsContainer.innerHTML = '';
        resultsContainer.hidden = false;
      }

      if (emptyResults) {
        emptyResults.hidden = true;
      }

      setStatus(
        'Select one answer to continue'
      );

      updateProgress();
      updateBackVisibility();

      showScreen('intro');
    };

    const handleBack = () => {
      clearTimers();

      if (state.currentStep <= 0) {
        return;
      }

      state.currentStep -= 1;

      showCurrentQuestion();
    };

    const handleAnswerKeydown = (
      event,
      button
    ) => {
      const supportedKeys = [
        'ArrowRight',
        'ArrowDown',
        'ArrowLeft',
        'ArrowUp',
        'Home',
        'End'
      ];

      if (
        !supportedKeys.includes(
          event.key
        )
      ) {
        return;
      }

      const dimension =
        button.dataset.dimension;

      const groupButtons =
        answerButtons.filter(
          (candidate) => {
            return (
              candidate.dataset.dimension ===
              dimension
            );
          }
        );

      const currentIndex =
        groupButtons.indexOf(button);

      if (currentIndex < 0) {
        return;
      }

      event.preventDefault();

      let targetIndex =
        currentIndex;

      if (
        event.key === 'ArrowRight' ||
        event.key === 'ArrowDown'
      ) {
        targetIndex =
          (
            currentIndex + 1
          ) %
          groupButtons.length;
      }

      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowUp'
      ) {
        targetIndex =
          (
            currentIndex -
            1 +
            groupButtons.length
          ) %
          groupButtons.length;
      }

      if (event.key === 'Home') {
        targetIndex = 0;
      }

      if (event.key === 'End') {
        targetIndex =
          groupButtons.length - 1;
      }

      groupButtons[
        targetIndex
      ].focus();
    };

    startButton.addEventListener(
      'click',
      () => {
        clearTimers();

        state.currentStep = 0;

        showCurrentQuestion();
      }
    );

    answerButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          handleAnswerClick(button);
        }
      );

      button.addEventListener(
        'keydown',
        (event) => {
          handleAnswerKeydown(
            event,
            button
          );
        }
      );
    });

    backButton?.addEventListener(
      'click',
      handleBack
    );

    restartButtons.forEach((button) => {
      button.addEventListener(
        'click',
        resetGuide
      );
    });

    updateProgress();
    updateBackVisibility();

    showScreen('intro', {
      focus: false,
      scroll: false
    });
  };

  const initAllGuides = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        SELECTORS.root
      )
    ) {
      initGuide(scope);
    }

    scope
      .querySelectorAll?.(
        SELECTORS.root
      )
      .forEach(initGuide);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAllGuides();
      }
    );
  } else {
    initAllGuides();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAllGuides(
        event.target
      );
    }
  );
})();
